/**
 * Режим ТЦ (mall-пресет, референс MallExplorer): вертикальный «лифт»-селектор
 * этажей слева + каталог магазинов активного этажа. Каталог: на десктопе —
 * боковая панель, на мобиле — оверлей по кнопке ☰ в селекторе.
 * Выбор этажа фильтрует venue-слои (venue-map.setLevel), как floor-switcher.
 */
import { getLang, tr } from '@app/i18n'
import { floorNumberOfLevel, levelTenantCount, localized, type Unit, type Venue } from '@app/rvf'
import { terraCategory } from '@app/tokens'
import { useState } from 'preact/hooks'
import { selectTarget } from '../actions'
import { promoTiers } from '../promo'
import { activeLevelId, langTick, mallDirectoryOpen, targetId, venue, venueMap } from '../state'
import { categoryLabel } from './labels'

function catColor(category: string): string {
  return (terraCategory as Record<string, string>)[category] ?? terraCategory.__default
}

/**
 * Подпись этажа. Номер, а не имя уровня: у Ülemiste уровни названы «0. korrus»,
 * и панель показывала «0/1/2», пока этажерка и «Объём 3D» считали «1/2/3» —
 * одно и то же место называлось по-разному (фидбек владельца 29.07).
 * Собственное имя уровня («Паркинг», «Цоколь») уважается, если оно задано и не
 * является просто номером. Фолбэк-номер — floorNumberOfLevel: как на табличках
 * в здании (−1/1/2, без нуля), а не индекс уровня в массиве.
 */
function levelLabel(v: Venue, levelId: string): string {
  const level = v.levels.find((l) => l.id === levelId)
  if (!level) return levelId
  const own = level.name ? localized(level.name, getLang(), v.venue.default_language) : ''
  const numbered = /^\s*-?\d+([.,]|\s|$)/.test(own)
  if (own && !numbered) return own
  return tr('volume3d.floor_n', { n: String(floorNumberOfLevel(v, levelId)) })
}

function unitName(v: Venue, unit: Unit): string {
  return localized(unit.name, getLang(), v.venue.default_language)
}

/** Вертикальный селектор этажей (лифт-стиль): NF сверху вниз + счётчики. */
function FloorRail({ v }: { v: Venue }) {
  // Подписка на язык нужна КАЖДОМУ компоненту, который сам зовёт tr(): чтение
  // сигнала в родителе подписывает только родителя.
  langTick.value
  const active = activeLevelId.value
  const levels = [...v.levels].sort((a, b) => b.ordinal - a.ordinal)

  const pick = (levelId: string): void => {
    const next = active === levelId ? null : levelId
    activeLevelId.value = next
    venueMap.value?.setLevel(next)
  }

  return (
    <nav class="mall-rail glass" aria-label={tr('floor.title')}>
      <div class="mall-rail-head">
        <span class="tech-label">{tr('mall.levels')}</span>
        <b>{v.levels.length}F</b>
      </div>
      {/* «Все» — явный пункт: снимать выбор повторным нажатием по этажу было
          неочевидно, а поиск по всему центру нужен чаще всего. */}
      <button
        type="button"
        class={`mall-floor${active === null ? ' mall-floor-on' : ''}`}
        onClick={() => {
          activeLevelId.value = null
          venueMap.value?.setLevel(null)
        }}
      >
        <span class="mall-floor-name">{tr('mall.all_short')}</span>
        <span class="mall-floor-count">
          {v.levels.reduce((s, l) => s + levelTenantCount(v, l.id), 0)}
        </span>
      </button>
      {levels.map((level) => {
        // Тот же счётчик, что в этажерке и «Объёме 3D» (floor-occupants):
        // раньше здесь считались все юниты уровня, включая само здание.
        const count = levelTenantCount(v, level.id)
        const on = active === level.id
        return (
          <button
            key={level.id}
            type="button"
            class={`mall-floor${on ? ' mall-floor-on' : ''}`}
            onClick={() => pick(level.id)}
          >
            <span class="mall-floor-name">{levelLabel(v, level.id)}</span>
            <span class="mall-floor-count">{count > 0 ? count : '—'}</span>
          </button>
        )
      })}
      <button
        type="button"
        class="mall-floor mall-dir-btn"
        aria-label={tr('mall.directory')}
        onClick={() => {
          mallDirectoryOpen.value = !mallDirectoryOpen.value
        }}
      >
        ☰
      </button>
    </nav>
  )
}

/** Каталог магазинов активного этажа: поиск + список, тап → карточка цели. */
function Directory({ v }: { v: Venue }) {
  langTick.value
  const [q, setQ] = useState('')
  const active = activeLevelId.value
  const units = v.units
    .filter((u) => (active ? u.level === active : true))
    .filter((u) => {
      const s = q.trim().toLowerCase()
      if (!s) return true
      return (
        unitName(v, u).toLowerCase().includes(s) ||
        (u.search_aliases ?? []).some((a) => a.toLowerCase().includes(s))
      )
    })
    .sort((a, b) => unitName(v, a).localeCompare(unitName(v, b)))

  const floorTitle = active ? levelLabel(v, active) : tr('mall.all_levels')

  return (
    <aside class="mall-dir glass anim-drawer">
      <div class="mall-dir-head">
        <div>
          <p class="tech-label mall-dir-mode">{tr('mall.mode')}</p>
          <h2 class="mall-dir-title">{floorTitle}</h2>
          <p class="tech-label mall-dir-sub">
            {tr('mall.floor_stats', { n: String(units.length) })}
          </p>
        </div>
        <button
          type="button"
          class="icon-btn"
          aria-label={tr('common.close')}
          onClick={() => {
            mallDirectoryOpen.value = false
          }}
        >
          ✕
        </button>
      </div>
      <input
        class="mall-dir-search"
        placeholder={tr('mall.search_placeholder')}
        value={q}
        onInput={(e) => setQ((e.target as HTMLInputElement).value)}
      />
      <div class="mall-dir-list">
        {units.length === 0 && <p class="mall-dir-empty">{tr('mall.empty')}</p>}
        {units.map((u) => (
          <button
            key={u.id}
            type="button"
            class={`mall-dir-row${promoTiers.value.has(u.id) ? ' promo-row' : ''}`}
            // Наведение подсвечивает объект на карте — глазами найти быстрее,
            // чем читать список из сотни строк.
            onPointerEnter={() => venueMap.value?.highlight(u.id)}
            onFocus={() => venueMap.value?.highlight(u.id)}
            onPointerLeave={() => venueMap.value?.highlight(targetId.value)}
            onClick={() => {
              mallDirectoryOpen.value = false
              // Выбрали магазин с другого этажа — переключаем и этаж, иначе он
              // остаётся невидимым под фильтром уровня.
              if (activeLevelId.value !== null && activeLevelId.value !== u.level) {
                activeLevelId.value = u.level
                venueMap.value?.setLevel(u.level)
              }
              selectTarget(u.id)
            }}
          >
            <span class="mall-dir-ico" style={{ color: catColor(u.category) }}>
              ●
            </span>
            <span class="mall-dir-name">
              <b>{unitName(v, u)}</b>
              <i class="tech-label">
                {categoryLabel(u.category)}
                {active === null ? ` · ${levelLabel(v, u.level)}` : ''}
              </i>
              {promoTiers.value.has(u.id) && <i class="promo-chip">{tr('promo.chip')}</i>}
            </span>
            <span class="mall-dir-arrow" aria-hidden="true">
              ›
            </span>
          </button>
        ))}
      </div>
    </aside>
  )
}

export function MallExplorer() {
  langTick.value
  const v = venue.value
  if (!v || v.levels.length < 2) return null
  return (
    <>
      <FloorRail v={v} />
      {mallDirectoryOpen.value && <Directory v={v} />}
    </>
  )
}
